import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Telestrator } from "./Telestrator";
import {
  Play, Pause, SkipBack, SkipForward, Rewind, FastForward,
  Maximize, Minimize, Volume2, VolumeX, Loader2,
} from "lucide-react";

const SEEK_STEP = 5; // seconds for rewind / fast-forward

function fmt(s) {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function ClipPlayer({ clip, videoUrl, loading, onPrev, onNext, hasPrev, hasNext }) {
  const videoRef = useRef(null);
  const resumeRef = useRef(null); // {time, playing} to survive the windowed<->fullscreen remount
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [fs, setFs] = useState(false);

  const isOff = clip.unit === "offense";

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const seekBy = useCallback((delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + delta));
  }, []);

  const onSeekInput = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const t = (Number(e.target.value) / 100) * (v.duration || 0);
    v.currentTime = t;
    setCurrent(t);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  // CSS-based full-viewport mode via portal -> escapes transformed/overflow-hidden
  // ancestors (works on iOS Safari where div requestFullscreen is unsupported).
  const toggleFullscreen = () => {
    const v = videoRef.current;
    if (v) resumeRef.current = { time: v.currentTime, playing: !v.paused, muted: v.muted };
    setFs((x) => !x);
  };

  useEffect(() => {
    if (!fs) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") toggleFullscreen(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fs]);

  // reset transport only when the actual clip source changes (not on fs toggle)
  useEffect(() => {
    if (resumeRef.current) return; // a fs toggle is in progress; keep position
    setCurrent(0); setDuration(0); setPlaying(false);
  }, [videoUrl]);

  const pct = duration > 0 ? (current / duration) * 100 : 0;

  const Btn = ({ onClick, disabled, testid, size = "md", children }) => {
    const dims = size === "xl" ? "h-16 w-16" : size === "lg" ? "h-14 w-14" : "h-11 w-11";
    const tone = size === "xl"
      ? "bg-primary hover:bg-red-500 text-white"
      : "bg-white/10 hover:bg-white/25 text-white backdrop-blur-sm";
    return (
      <button data-testid={testid} onClick={onClick} disabled={disabled}
        className={`${dims} ${tone} flex items-center justify-center rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}>
        {children}
      </button>
    );
  };

  const PlayIcon = playing
    ? <Pause className="h-7 w-7 fill-current" strokeWidth={1} />
    : <Play className="h-7 w-7 fill-current ml-0.5" strokeWidth={1} />;

  const onLoadedMeta = (e) => {
    setDuration(e.target.duration || 0);
    const r = resumeRef.current;
    if (r) {
      try { e.target.currentTime = r.time; } catch {}
      e.target.muted = r.muted;
      if (r.playing) e.target.play().catch(() => {});
      resumeRef.current = null;
    }
  };

  const VideoEl = loading ? (
    <div className="absolute inset-0 flex items-center justify-center z-10">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  ) : (
    <>
      <video ref={videoRef} src={videoUrl} data-testid="clip-player" autoPlay playsInline
        onClick={togglePlay}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
        onLoadedMetadata={onLoadedMeta}
        onEnded={() => (hasNext ? onNext() : setPlaying(false))}
        className={fs ? "z-10 max-h-[100dvh] max-w-full w-auto h-full object-contain" : "z-10 absolute inset-0 h-full w-full"} />
      <Telestrator key={clip.id + (fs ? "-fs" : "")} />
    </>
  );

  const MetaPill = (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/70 backdrop-blur-sm rounded-full px-3.5 py-1.5 border border-white/10">
      <span className={`font-heading text-sm font-black tabular-nums ${isOff ? "text-primary" : "text-secondary"}`}>#{clip.clip_number}</span>
      <span className="h-3 w-px bg-white/25" />
      <span className="text-xs font-bold uppercase tracking-widest text-white">Q{clip.quarter}</span>
      <span className="h-3 w-px bg-white/25" />
      <span className={`text-xs font-bold uppercase tracking-widest ${isOff ? "text-primary" : "text-secondary"}`}>{clip.unit}</span>
    </div>
  );

  // ---------- FULLSCREEN (portal to body) ----------
  if (fs) {
    return createPortal(
      <div data-testid="clip-player-wrap"
        className="fixed inset-0 z-[9999] bg-black w-screen h-[100dvh] flex items-center justify-center">
        {VideoEl}
        {MetaPill}

        <div className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-3">
          <Btn testid="fs-skip-back" onClick={onPrev} disabled={!hasPrev} size="lg"><SkipBack className="h-6 w-6" strokeWidth={2.5} /></Btn>
          <Btn testid="fs-rewind" onClick={() => seekBy(-SEEK_STEP)} size="lg"><Rewind className="h-6 w-6" strokeWidth={2.5} /></Btn>
          <Btn testid="fs-play-left" onClick={togglePlay} size="xl">{PlayIcon}</Btn>
        </div>

        <div className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-3">
          <Btn testid="fs-skip-forward" onClick={onNext} disabled={!hasNext} size="lg"><SkipForward className="h-6 w-6" strokeWidth={2.5} /></Btn>
          <Btn testid="fs-forward" onClick={() => seekBy(SEEK_STEP)} size="lg"><FastForward className="h-6 w-6" strokeWidth={2.5} /></Btn>
          <Btn testid="fs-play-right" onClick={togglePlay} size="xl">{PlayIcon}</Btn>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black/90 to-transparent px-4 sm:px-8 pb-4 pt-10">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold tabular-nums text-white/90 w-10 text-right">{fmt(current)}</span>
            <input type="range" min={0} max={100} step={0.1} value={pct} onChange={onSeekInput}
              data-testid="player-seek" aria-label="Seek"
              className="flex-1 h-1.5 appearance-none rounded-full cursor-pointer accent-primary"
              style={{ background: `linear-gradient(to right, #FF3B30 ${pct}%, rgba(255,255,255,0.25) ${pct}%)` }} />
            <span className="text-[11px] font-bold tabular-nums text-white/60 w-10">{fmt(duration)}</span>
            <button data-testid="player-mute" onClick={toggleMute}
              className="h-11 w-11 rounded-md bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors shrink-0">
              {muted ? <VolumeX className="h-5 w-5" strokeWidth={2.5} /> : <Volume2 className="h-5 w-5" strokeWidth={2.5} />}
            </button>
            <button data-testid="player-fullscreen" onClick={toggleFullscreen}
              className="h-11 w-11 rounded-md bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors shrink-0">
              <Minimize className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ---------- WINDOWED ----------
  return (
    <div data-testid="clip-player-wrap" className="relative bg-black w-full aspect-video">
      {VideoEl}
      {MetaPill}

      <button data-testid="skip-prev-edge" onClick={onPrev} disabled={!hasPrev}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-40 h-12 w-12 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center transition-colors disabled:opacity-0">
        <SkipBack className="h-6 w-6" strokeWidth={2.5} />
      </button>
      <button data-testid="skip-next-edge" onClick={onNext} disabled={!hasNext}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-40 h-12 w-12 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center transition-colors disabled:opacity-0">
        <SkipForward className="h-6 w-6" strokeWidth={2.5} />
      </button>

      <div className="absolute bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pb-3 pt-8">
        <div className="flex items-center gap-3 mb-2.5">
          <span className="text-[11px] font-bold tabular-nums text-white/90 w-10 text-right">{fmt(current)}</span>
          <input type="range" min={0} max={100} step={0.1} value={pct} onChange={onSeekInput}
            data-testid="player-seek" aria-label="Seek"
            className="flex-1 h-1.5 appearance-none rounded-full cursor-pointer accent-primary"
            style={{ background: `linear-gradient(to right, #FF3B30 ${pct}%, rgba(255,255,255,0.25) ${pct}%)` }} />
          <span className="text-[11px] font-bold tabular-nums text-white/60 w-10">{fmt(duration)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex-1 flex items-center justify-center gap-2 sm:gap-3">
            <Btn testid="player-skip-back" onClick={onPrev} disabled={!hasPrev}><SkipBack className="h-5 w-5" strokeWidth={2.5} /></Btn>
            <Btn testid="player-rewind" onClick={() => seekBy(-SEEK_STEP)}><Rewind className="h-5 w-5" strokeWidth={2.5} /></Btn>
            <Btn testid="player-playpause" onClick={togglePlay} size="lg">{PlayIcon}</Btn>
            <Btn testid="player-forward" onClick={() => seekBy(SEEK_STEP)}><FastForward className="h-5 w-5" strokeWidth={2.5} /></Btn>
            <Btn testid="player-skip-forward" onClick={onNext} disabled={!hasNext}><SkipForward className="h-5 w-5" strokeWidth={2.5} /></Btn>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="player-mute" onClick={toggleMute}
              className="h-11 w-11 rounded-md bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors">
              {muted ? <VolumeX className="h-5 w-5" strokeWidth={2.5} /> : <Volume2 className="h-5 w-5" strokeWidth={2.5} />}
            </button>
            <button data-testid="player-fullscreen" onClick={toggleFullscreen}
              className="h-11 w-11 rounded-md bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors">
              <Maximize className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
